import mixpanel from 'mixpanel-browser';

const ANALYTICS_SCHEMA_VERSION = 1;
const ATTRIBUTION_STORAGE_KEY = 'practice_meter_attribution_v1';
const MIXPANEL_TOKEN =
  process.env.REACT_APP_MIXPANEL_TOKEN || '25198c1b844ff40fc562192487f7bf5f';

export const ANALYTICS_EXCLUDED_EMAILS = [
  'quincy.c.delp@gmail.com',
  'qpolarbear@gmail.com',
];

const CAMPAIGN_PARAMETERS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
];

const normalizeEmail = (email = '') => String(email).trim().toLowerCase();

export const isAnalyticsExcludedEmail = (email) =>
  ANALYTICS_EXCLUDED_EMAILS.includes(normalizeEmail(email));

const readJson = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const sanitizeError = (error) => {
  const raw = error?.message || String(error || 'Unknown error');
  return raw
    .replace(/Bearer\s+\S+/gi, 'Bearer [redacted]')
    .replace(/[?&](token|code|session_id)=[^&\s]+/gi, '$1=[redacted]')
    .slice(0, 240);
};

const canonicalFailureMode = (error) => {
  const value = sanitizeError(error).toLowerCase();
  if (/timeout|timed out/.test(value)) return 'timeout';
  if (/network|failed to fetch|connect|offline/.test(value)) return 'network_error';
  if (/permission|denied|not allowed/.test(value)) return 'permission_denied';
  if (/expired/.test(value)) return 'expired';
  if (/already|exists/.test(value)) return 'already_exists';
  if (/invalid|incorrect|password|credential/.test(value)) return 'invalid_input_or_credentials';
  if (/not enough|insufficient/.test(value)) return 'insufficient_data';
  if (/not found|disconnected|no midi|no device/.test(value)) return 'not_found_or_disconnected';
  if (/classif/.test(value)) return 'classification_failed';
  if (/store|storage|save/.test(value)) return 'storage_failed';
  if (/server|internal/.test(value)) return 'server_error';

  return value
    .replace(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, '[redacted_email]')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'unknown';
};

const endpointFromUrl = (input) => {
  try {
    const raw = typeof input === 'string' ? input : input?.url;
    const url = new URL(raw, window.location.origin);
    return url.pathname
      .replace(/\/\d+(?=\/|$)/g, '/:id')
      .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ':id');
  } catch {
    return 'unknown';
  }
};

const isExpectedApiResponse = (endpoint, status) =>
  (endpoint === '/api/auth/me' && [401, 403].includes(status)) ||
  (endpoint === '/api/subscriptions/info' && status === 404);

const collectAttribution = () => {
  const params = new URLSearchParams(window.location.search);
  const captured = {};

  CAMPAIGN_PARAMETERS.forEach((key) => {
    const value = params.get(key);
    if (value) captured[key] = value;
  });

  const bridgeProperties = {
    landing_page: params.get('pm_landing_page'),
    landing_path: params.get('pm_landing_path'),
    landing_referrer: params.get('pm_referrer'),
    landing_cta: params.get('pm_cta'),
  };

  Object.entries(bridgeProperties).forEach(([key, value]) => {
    if (value) captured[key] = value;
  });

  if (!captured.landing_page) {
    captured.landing_page = `${window.location.origin}${window.location.pathname}`;
  }
  if (!captured.landing_referrer && document.referrer) {
    captured.landing_referrer = document.referrer;
  }

  const existing = readJson(ATTRIBUTION_STORAGE_KEY);
  const attribution = existing
    ? { ...existing, ...captured, last_touch_at: new Date().toISOString() }
    : {
        ...captured,
        first_touch_at: new Date().toISOString(),
        first_touch_path: window.location.pathname,
      };

  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Analytics must never interfere with product behavior.
  }

  return attribution;
};

class MixpanelService {
  constructor() {
    this.isInitialized = false;
    this.isExcluded = false;
    this.currentUser = null;
    this.attribution = {};
    this.fetchInstrumented = false;
    this.globalErrorsInstrumented = false;
  }

  init(projectId = MIXPANEL_TOKEN) {
    if (this.isInitialized || typeof window === 'undefined') return;

    const cachedUser = readJson('user');
    if (isAnalyticsExcludedEmail(cachedUser?.email)) {
      this.isExcluded = true;
      return;
    }

    try {
      mixpanel.init(projectId, {
        debug: process.env.NODE_ENV === 'development',
        persistence: 'cookie',
        cross_subdomain_cookie: true,
        secure_cookie: window.location.protocol === 'https:',
        track_pageview: false,
        autocapture: {
          pageview: false,
          click: true,
          input: true,
          submit: true,
          scroll: true,
          rage_click: true,
          capture_text_content: false,
          block_selectors: [
            'input[type="password"]',
            '[data-analytics-private]',
            '.mp-sensitive',
          ],
        },
      });

      this.isInitialized = true;
      const bridgedAnonymousId = new URLSearchParams(window.location.search).get('mp_anon_id');
      if (bridgedAnonymousId) {
        mixpanel.identify(bridgedAnonymousId);
      }
      this.attribution = collectAttribution();

      mixpanel.register({
        analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
        product: 'Practice Meter',
        surface: 'application',
        ...this.attribution,
      });

      this.installFetchInstrumentation();
      this.installGlobalErrorInstrumentation();
    } catch (error) {
      // Analytics failures must never interrupt the application.
      console.error('Failed to initialize Mixpanel:', error);
    }
  }

  setUserContext(user = {}) {
    this.currentUser = user;

    if (isAnalyticsExcludedEmail(user.email)) {
      this.isExcluded = true;
      if (this.isInitialized) {
        mixpanel.opt_out_tracking({ clear_persistence: true });
      }
      return false;
    }

    return true;
  }

  identifyUser(userId, userProperties = {}) {
    if (!this.setUserContext(userProperties) || !this.canTrack()) return;

    try {
      const distinctId = String(userId || userProperties.email);
      mixpanel.identify(distinctId);
      mixpanel.register({
        authenticated: true,
        user_role: userProperties.role || 'unknown',
      });
      mixpanel.people.set({
        $email: userProperties.email,
        $first_name: userProperties.firstName,
        $last_name: userProperties.lastName,
        $created: userProperties.createdAt,
        role: userProperties.role || 'unknown',
        subscription_status: userProperties.subscriptionStatus || 'unknown',
        analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
      });
      mixpanel.people.set_once({
        first_touch_source: this.attribution.utm_source || 'direct',
        first_touch_medium: this.attribution.utm_medium || 'none',
        first_touch_campaign: this.attribution.utm_campaign || 'none',
        first_landing_page: this.attribution.landing_page,
        first_landing_referrer: this.attribution.landing_referrer || 'direct',
      });
    } catch (error) {
      console.error('Error identifying Mixpanel user:', error);
    }
  }

  canTrack() {
    return this.isInitialized && !this.isExcluded;
  }

  track(eventName, properties = {}) {
    if (!this.canTrack()) return;

    try {
      const normalizedProperties = { ...properties };
      if (normalizedProperties.failure_mode) {
        normalizedProperties.failure_mode = canonicalFailureMode(
          normalizedProperties.failure_mode
        );
      }

      mixpanel.track(eventName, {
        analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
        path: window.location.pathname,
        authenticated: Boolean(this.currentUser),
        user_role: this.currentUser?.role || 'anonymous',
        ...normalizedProperties,
      });
    } catch (error) {
      console.error(`Error tracking ${eventName}:`, error);
    }
  }

  trackPageView(screenName, properties = {}) {
    this.track('Screen Viewed', {
      screen_name: screenName,
      url: window.location.href,
      ...properties,
    });
  }

  trackFailure(operation, error, properties = {}) {
    this.track('Operation Failed', {
      operation,
      failure_mode: canonicalFailureMode(error),
      ...properties,
    });
  }

  trackLogin(success = true, properties = {}) {
    this.track(success ? 'Login Completed' : 'Login Started', properties);
  }

  trackSignUp(success = true, properties = {}) {
    this.track(success ? 'Sign Up Completed' : 'Sign Up Started', properties);
  }

  trackPracticeStart(properties = {}) {
    this.track('Practice Session Started', properties);
  }

  trackPracticeCompleted(properties = {}) {
    this.track('Practice Session Completed', properties);
    if (this.canTrack()) {
      mixpanel.people.increment('lifetime_practice_sessions', 1);
      mixpanel.people.set({ last_practice_at: new Date().toISOString() });
    }
  }

  resetUser() {
    if (!this.canTrack()) return;

    try {
      this.track('Logout Completed');
      mixpanel.reset();
      this.currentUser = null;
      this.attribution = collectAttribution();
      mixpanel.register({
        analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
        product: 'Practice Meter',
        surface: 'application',
        ...this.attribution,
      });
    } catch (error) {
      console.error('Error resetting Mixpanel identity:', error);
    }
  }

  installFetchInstrumentation() {
    if (this.fetchInstrumented || !window.fetch) return;
    this.fetchInstrumented = true;
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
      const endpoint = endpointFromUrl(input);
      const method = String(init.method || 'GET').toUpperCase();
      const startedAt = performance.now();

      try {
        const response = await originalFetch(input, init);
        if (
          !response.ok &&
          !endpoint.includes('mixpanel') &&
          !isExpectedApiResponse(endpoint, response.status)
        ) {
          this.track('API Request Failed', {
            endpoint,
            method,
            status_code: response.status,
            duration_ms: Math.round(performance.now() - startedAt),
          });
        }
        return response;
      } catch (error) {
        this.track('API Request Failed', {
          endpoint,
          method,
          status_code: 0,
          duration_ms: Math.round(performance.now() - startedAt),
          failure_mode: sanitizeError(error),
        });
        throw error;
      }
    };
  }

  installGlobalErrorInstrumentation() {
    if (this.globalErrorsInstrumented) return;
    this.globalErrorsInstrumented = true;

    window.addEventListener('error', (event) => {
      this.track('Application Error', {
        failure_mode: sanitizeError(event.error || event.message),
        source: 'window_error',
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.track('Application Error', {
        failure_mode: sanitizeError(event.reason),
        source: 'unhandled_promise_rejection',
      });
    });
  }
}

const mixpanelService = new MixpanelService();

export default mixpanelService;
